// Planning helpers for the setup guide: suggested names, dates, goals and
// rewards, and advice on the dates an admin picks. Pure functions over
// ISO dates ("2026-10-19"), computed in UTC so a date never shifts with
// the browser's timezone.
//
// The advice follows PagePledge's published planning guide
// (/read-a-thon) and help centre — keep them in step.

const DAY_MS = 86_400_000;

// Defaults; each campaign can choose its own (lib/campaignWindows).
export const DONATION_WINDOW_DAYS = 14;
export const GRACE_DAYS = 7;
// A week to get set up before donations open.
const SETUP_RUNWAY_DAYS = 7;
// "Most children read 15–20 minutes a day already … add 30–50% as a
// stretch" (help: Setting a personal goal).
export const MINUTES_PER_DAY = 20;
export const STRETCH = 0.3;

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ── Dates ─────────────────────────────────────────────────────

export function parseDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function toIso(date) {
  return date.toISOString().slice(0, 10);
}

export function addDays(iso, days) {
  return toIso(new Date(parseDate(iso).getTime() + days * DAY_MS));
}

export function daysBetween(fromIso, toIsoDate) {
  return Math.round((parseDate(toIsoDate) - parseDate(fromIso)) / DAY_MS);
}

export function weekdayName(iso) {
  return WEEKDAYS[parseDate(iso).getUTCDay()];
}

function isWeekend(iso) {
  const day = parseDate(iso).getUTCDay();
  return day === 0 || day === 6;
}

// The Monday on or after the date.
export function mondayOnOrAfter(iso) {
  const day = parseDate(iso).getUTCDay();
  return addDays(iso, (8 - day) % 7);
}

// The Friday on or before the date.
function fridayOnOrBefore(iso) {
  const day = parseDate(iso).getUTCDay();
  return addDays(iso, -((day + 2) % 7));
}

// "Mon, Oct 19"
export function formatDay(iso) {
  if (!iso) return "";
  return parseDate(iso).toLocaleDateString("en-CA", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });
}

// "Oct 19"
export function formatShortDate(iso) {
  if (!iso) return "";
  return parseDate(iso).toLocaleDateString("en-CA", { timeZone: "UTC", month: "short", day: "numeric" });
}

// "in 9 days", "tomorrow", "today", "3 days ago"
export function relativeDays(today, iso) {
  const days = daysBetween(today, iso);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

// The dates families and teachers will hear about, for a start and end
// and the campaign's windows (see lib/campaignWindows). A null
// donation_lead_days means donations open any time before reading: from
// when a saved campaign opened them (`savedOpenOn`), else today, but
// never after the first day of reading.
export function keyDatesFor(start, end, windows = {}, today = toIso(new Date()), savedOpenOn = null) {
  // A field left blank mid-edit falls back to the default.
  const days = (value, fallback) => (Number.isInteger(value) ? value : fallback);
  const anyTime = windows.donation_lead_days === null;
  const lead = days(windows.donation_lead_days, DONATION_WINDOW_DAYS);
  const logging = days(windows.minute_logging_days, 1);
  const after = days(windows.donation_days_after, GRACE_DAYS);
  return {
    donationsOpen: anyTime ? [savedOpenOn || today, start].sort()[0] : addDays(start, -lead),
    donationsOpenAnyTime: anyTime,
    start,
    end,
    loggingCloses: addDays(end, logging),
    loggingClosesAtEndOfDay: logging === 0,
    donationsClose: addDays(end, after),
  };
}

// ── Suggestions ───────────────────────────────────────────────

// "Fall Read-a-thon 2026", from the season the campaign starts in.
export function suggestCampaignName(start) {
  const date = parseDate(start);
  const month = date.getUTCMonth();
  const season = month >= 8 && month <= 10 ? "Fall" : month === 11 || month <= 1 ? "Winter" : month <= 5 ? "Spring" : "Summer";
  return `${season} Read-a-thon ${date.getUTCFullYear()}`;
}

// Dates the admin gave us when they signed up, if still possible;
// otherwise a Monday-to-Friday fortnight with a week to set up and the
// full two weeks of early donations before reading starts.
export function suggestDates({ today, earliestStart, applicationStart, applicationEnd }) {
  const fitsSchedule = (iso) => iso > today && (!earliestStart || iso >= earliestStart);

  if (applicationStart && fitsSchedule(applicationStart)) {
    const end = applicationEnd && applicationEnd > applicationStart ? applicationEnd : addDays(applicationStart, 11);
    return { start: applicationStart, end, fromApplication: true };
  }

  const runway = addDays(today, DONATION_WINDOW_DAYS + SETUP_RUNWAY_DAYS);
  const start = mondayOnOrAfter(earliestStart && earliestStart > runway ? earliestStart : runway);
  return { start, end: addDays(start, 11), fromApplication: false };
}

// Gentle advice on a start and end date. Each item may carry a `fix`:
// replacement dates the admin can apply in one click.
// With donations open any time, `leadDays` is null and the runway advice
// is about the take-home sheets going home two weeks ahead instead.
export function dateAdvice({ start, end, today, leadDays = DONATION_WINDOW_DAYS }) {
  const anyTime = leadDays === null;
  if (anyTime) leadDays = DONATION_WINDOW_DAYS;
  if (!start || !end || end < start) return [];

  const advice = [];
  const length = daysBetween(start, end) + 1;
  const lead = daysBetween(today, start);

  if (lead <= 0) {
    advice.push({ tone: "warning", text: "That start date isn't in the future." });
  } else if (leadDays > 0 && lead < leadDays) {
    const better = mondayOnOrAfter(addDays(today, leadDays + SETUP_RUNWAY_DAYS));
    advice.push({
      tone: "warning",
      text: `${anyTime ? `Take-home sheets should go home about ${leadDays} days before reading starts` : `Donations open ${leadDays} days before reading starts`}, so families can share their page with grandparents first. Starting ${formatDay(start)} leaves ${lead} day${lead === 1 ? "" : "s"} for that — and for you to get set up.`,
      fix: { label: `Start ${formatDay(better)} instead`, start: better, end: addDays(better, length - 1) },
    });
  }

  if (length < 7) {
    advice.push({ tone: "info", text: "Two weeks is a practical starting plan: a launch, a progress update and a finish. Under a week leaves little room for the middle." });
  } else if (length > 21) {
    advice.push({ tone: "info", text: "Two weeks is a practical starting plan: a launch, a progress update and a finish. A longer campaign works too — plan an extra update so families keep logging." });
  }

  // The runway fix above already lands on a Monday.
  if (isWeekend(start) && lead >= leadDays) {
    const monday = mondayOnOrAfter(start);
    advice.push({
      tone: "info",
      text: `Starts on a ${weekdayName(start)}. A Monday start gives classrooms a clear kickoff.`,
      fix: { label: `Start ${formatDay(monday)}`, start: monday, end: addDays(end, daysBetween(start, monday)) },
    });
  }

  if (isWeekend(end)) {
    const friday = fridayOnOrBefore(end);
    if (friday > start) {
      advice.push({
        tone: "info",
        text: `Ends on a ${weekdayName(end)}. A Friday finish lets classrooms celebrate together.`,
        fix: { label: `End ${formatDay(friday)}`, start, end: friday },
      });
    }
  }

  return advice;
}

// Students × 20 minutes a day × campaign days, plus a 30% stretch.
export function suggestMinuteGoal({ students, start, end }) {
  if (!students || !start || !end || end < start) return null;

  const days = daysBetween(start, end) + 1;
  const baseline = students * MINUTES_PER_DAY * days;
  return { students, days, baseline, goal: roundNicely(baseline * (1 + STRETCH)) };
}

// School-wide rewards sized to the goals: a quick early win, a midpoint,
// and a big finish at the goal. Prize ideas from /readathon-fundraising-ideas.
export function suggestSchoolRewards({ moneyGoalCents, minuteGoal }) {
  const rewards = [];

  if (moneyGoalCents > 0) {
    const dollars = moneyGoalCents / 100;
    rewards.push({ kind: "money", amount: roundMoney(dollars * 0.25), prize: "Students vote on the next read-aloud" });
    rewards.push({ kind: "money", amount: roundMoney(dollars * 0.5), prize: "Pajama reading day" });
    rewards.push({ kind: "money", amount: roundMoney(dollars), prize: "The principal performs a favourite story in costume" });
  }
  if (minuteGoal > 0) {
    rewards.push({ kind: "minutes", amount: roundNicely(minuteGoal), prize: "Whole-school read-in afternoon" });
  }

  return rewards;
}

// "One that almost everyone can reach, one that's a stretch, and one
// that's a real achievement" (help: Student prizes) — for reading, so
// every child can earn them whatever their family gives.
export function suggestStudentPrizes({ start, end }) {
  if (!start || !end || end < start) return [];

  const days = daysBetween(start, end) + 1;
  return [
    { kind: "minutes", amount: roundNicely(10 * days), prize: "A special bookmark" },
    { kind: "minutes", amount: roundNicely(MINUTES_PER_DAY * days), prize: "Pick from the prize bin" },
    { kind: "minutes", amount: roundNicely(30 * days), prize: "Choose a new book to keep" },
  ];
}

// ── Numbers ───────────────────────────────────────────────────

// Two significant figures: 88,608 → 89,000; 1,234 → 1,200; 118 → 120.
export function roundNicely(value) {
  if (value <= 0) return 0;
  const step = 10 ** Math.max(0, Math.floor(Math.log10(value)) - 1);
  return Math.round(value / step) * step;
}

// Dollar amounts on familiar steps: $50s under $1,000, $250s under
// $10,000, $1,000s above.
export function roundMoney(dollars) {
  const step = dollars < 1000 ? 50 : dollars < 10000 ? 250 : 1000;
  return Math.max(step, Math.round(dollars / step) * step);
}

export { formatMoney as formatDollars } from "@/lib/format";
export { formatThreshold } from "@/lib/prizes";
